import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line,
} from 'recharts';
import {
  LayoutDashboard, RefreshCw, Printer, IndianRupee, FileText, Wallet, Percent, TrendingUp,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import salesOverviewService from '../../../services/salesOverviewService';
import './SalesOverviewReport.css';

const formatINR = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Number(v) || 0);

const formatCompact = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return formatINR(n);
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="sov-tooltip">
      <div className="sov-tooltip__label">{label}</div>
      {payload.map((p) => {
        const isCount = p.dataKey === 'invoiceCount' || p.dataKey === 'paymentCount';
        return (
          <div key={p.dataKey} className="sov-tooltip__row" style={{ color: p.color || p.fill }}>
            <span>{p.name}</span>
            <strong>{isCount ? (p.value ?? 0) : formatINR(p.value)}</strong>
          </div>
        );
      })}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, tone = 'navy', to }) => (
  <button
    type="button"
    className={`sov-kpi sov-kpi--${tone}`}
    onClick={to ? () => to() : undefined}
    disabled={!to}
  >
    <div className="sov-kpi__icon"><Icon size={18} /></div>
    <div className="sov-kpi__body">
      <div className="sov-kpi__label">{label}</div>
      <div className="sov-kpi__value">{value}</div>
      {sub ? <div className="sov-kpi__sub">{sub}</div> : null}
    </div>
  </button>
);

const SalesOverviewReport = () => {
  const navigate = useNavigate();
  const [fy, setFy] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (financialYear) => {
    setLoading(true);
    try {
      const params = financialYear ? { financialYear } : {};
      const res = await salesOverviewService.getOverview(params);
      if (res?.success) {
        setData(res.data);
        if (!financialYear && res.data?.financialYear) setFy(res.data.financialYear);
      } else {
        setData(null);
        toast.error(res?.message || 'Failed to load overview');
      }
    } catch (e) {
      console.error(e);
      setData(null);
      toast.error(e?.response?.data?.message || 'Failed to load sales overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(fy || undefined); }, [fy, load]);

  const cards = data?.cards;
  const charts = data?.charts;

  const comparison = useMemo(() => charts?.invoicedVsCollected || [], [charts]);

  const breadcrumbItems = [
    { label: 'Sales', path: '/sales', onClick: () => navigate('/sales') },
    { label: 'Reports', path: '/sales?tab=reports', onClick: () => navigate('/sales?tab=reports') },
    { label: 'Overview', path: '/sales/reports/overview', isCurrent: true },
  ];

  const asOfLabel = data?.asOf
    ? new Date(data.asOf).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    })
    : '—';

  return (
    <div className="sales-dashboard sov-page">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="sov-hero">
        <div className="sov-hero__text">
          <div className="sov-hero__eyebrow"><LayoutDashboard size={16} /> Sales Overview</div>
          <h1>Invoicing &amp; Collections</h1>
          <p>
            Financial year performance — invoices raised vs money received.
            {data?.notes?.fy ? ` ${data.notes.fy}.` : ''}
          </p>
          <div className="sov-hero__meta">
            <span>As of {asOfLabel}</span>
            {cards?.collectionEfficiencyYtd != null && (
              <span className="sov-pill">
                YTD collection efficiency {cards.collectionEfficiencyYtd}%
              </span>
            )}
          </div>
        </div>
        <div className="sov-hero__controls">
          <label>
            Financial year
            <select
              className="sov-select"
              value={fy || data?.financialYear || ''}
              onChange={(e) => setFy(e.target.value)}
            >
              {(data?.fyOptions || [fy].filter(Boolean)).map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>
          <button type="button" className="sov-btn" onClick={() => load(fy || undefined)} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'sov-spin' : ''} /> Refresh
          </button>
          <button type="button" className="sov-btn sov-btn--ghost" onClick={() => window.print()}>
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="sov-loading">Loading sales overview…</div>
      )}

      {data && (
        <>
          <div className="sov-kpi-grid">
            <KpiCard
              icon={IndianRupee}
              label="Invoiced MTD"
              value={formatCompact(cards.invoicesMtd?.withTax)}
              sub={`${cards.invoicesMtd?.count || 0} invoices · GST ${formatCompact(cards.invoicesMtd?.gst)}`}
              tone="teal"
              to={() => navigate('/sales/reports/tax-invoice')}
            />
            <KpiCard
              icon={TrendingUp}
              label="Invoiced YTD"
              value={formatCompact(cards.invoicesYtd?.withTax)}
              sub={`${cards.invoicesYtd?.count || 0} invoices · Pre-tax ${formatCompact(cards.invoicesYtd?.taxable)}`}
              tone="navy"
              to={() => navigate('/sales/reports/sales-register')}
            />
            <KpiCard
              icon={FileText}
              label="GST MTD / YTD"
              value={formatCompact(cards.invoicesMtd?.gst)}
              sub={`YTD GST ${formatCompact(cards.invoicesYtd?.gst)}`}
              tone="amber"
            />
            <KpiCard
              icon={Wallet}
              label="Collected MTD"
              value={formatCompact(cards.collectionsMtd?.amount)}
              sub={`${cards.collectionsMtd?.count || 0} payments`}
              tone="green"
              to={() => navigate('/accounts/reports/outstanding-payments')}
            />
            <KpiCard
              icon={Wallet}
              label="Collected YTD"
              value={formatCompact(cards.collectionsYtd?.amount)}
              sub={`Cleared ${formatCompact(cards.collectionsYtd?.cleared)}`}
              tone="green"
            />
            <KpiCard
              icon={Percent}
              label="Outstanding"
              value={formatCompact(cards.outstanding?.amount)}
              sub={`${cards.outstanding?.openInvoiceCount || 0} open invoices`}
              tone="coral"
              to={() => navigate('/accounts/reports/invoice-aging')}
            />
          </div>

          <div className="sov-charts">
            <section className="sov-chart-card">
              <header className="sov-chart-card__head">
                <div>
                  <h2>Invoicing by month</h2>
                  <p>Tax invoices raised across FY {data.financialYear} (Apr–Mar cycle)</p>
                </div>
              </header>
              <div className="sov-chart-card__body">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={charts.invoicingByMonth} margin={{ top: 8, right: 28, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sovInvGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00A8D0" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#007A9A" stopOpacity={0.75} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 6" stroke="#E4DFEE" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: '#5B6478', fontSize: 11 }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={48} />
                    <YAxis
                      yAxisId="amt"
                      tickFormatter={(v) => formatCompact(v).replace('₹', '')}
                      tick={{ fill: '#8A93A6', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={56}
                    />
                    <YAxis
                      yAxisId="cnt"
                      orientation="right"
                      tick={{ fill: '#8A93A6', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                      allowDecimals={false}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,168,208,0.06)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="amt" dataKey="invoiced" name="Invoiced (with tax)" fill="url(#sovInvGrad)" radius={[6, 6, 0, 0]} maxBarSize={36} />
                    <Line yAxisId="cnt" type="monotone" dataKey="invoiceCount" name="Invoice count" stroke="#222448" strokeWidth={2} dot={{ r: 3, fill: '#222448' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="sov-chart-card">
              <header className="sov-chart-card__head">
                <div>
                  <h2>Collections by month</h2>
                  <p>Customer payments received when invoices are paid</p>
                </div>
              </header>
              <div className="sov-chart-card__body">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={charts.collectionsByMonth} margin={{ top: 8, right: 28, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sovColGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#059669" stopOpacity={0.8} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 6" stroke="#E4DFEE" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: '#5B6478', fontSize: 11 }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={48} />
                    <YAxis
                      yAxisId="amt"
                      tickFormatter={(v) => formatCompact(v).replace('₹', '')}
                      tick={{ fill: '#8A93A6', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={56}
                    />
                    <YAxis
                      yAxisId="cnt"
                      orientation="right"
                      tick={{ fill: '#8A93A6', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                      allowDecimals={false}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(16,185,129,0.06)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="amt" dataKey="collected" name="Collected" fill="url(#sovColGrad)" radius={[6, 6, 0, 0]} maxBarSize={36} />
                    <Line yAxisId="cnt" type="monotone" dataKey="paymentCount" name="Payments" stroke="#047857" strokeWidth={2} dot={{ r: 3, fill: '#047857' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>

          <section className="sov-chart-card sov-chart-card--wide">
            <header className="sov-chart-card__head">
              <div>
                <h2>Invoiced vs collected</h2>
                <p>Side-by-side view of billing and cash-in for each month of the financial year</p>
              </div>
            </header>
            <div className="sov-chart-card__body">
                <ResponsiveContainer width="100%" height={360}>
                <BarChart data={comparison} margin={{ top: 12, right: 16, left: 4, bottom: 0 }} barGap={4} barCategoryGap="18%">
                  <CartesianGrid strokeDasharray="3 6" stroke="#E4DFEE" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: '#5B6478', fontSize: 11 }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={48} />
                  <YAxis
                    tickFormatter={(v) => formatCompact(v).replace('₹', '')}
                    tick={{ fill: '#8A93A6', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={56}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(20,27,61,0.04)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="invoiced" name="Invoiced" fill="#00A8D0" radius={[4, 4, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="collected" name="Collected" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <footer className="sov-chart-card__foot">
              <span>{data.notes?.invoicing}</span>
              <span>{data.notes?.collections}</span>
            </footer>
          </section>
        </>
      )}
    </div>
  );
};

export default SalesOverviewReport;
